import { useRef, useState } from 'react'
import { Box, Button, HStack, Link, Text, VStack } from '@chakra-ui/react'
import { FiUpload, FiTrash2, FiEdit2 } from 'react-icons/fi'
import {
  useEventAttachments, useUploadAttachment, useUpdateAttachment, useDeleteAttachment,
} from '../../../hooks/useLobbyDay'
import { lobbyAttachmentApi } from '../../../lib/api'
import { extractApiMessage } from '../../../lib/apiError'
import { Card } from '../../shared/Card'
import type { LobbyDayEventDetailDto } from '../../../types/lobbyDay'

const CAUTION =
  "Visible to every attendee of this event. Don't upload rosters or member lists."

function formatMb(bytes: number): string {
  const mb = bytes / (1024 * 1024)
  return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`
}

/**
 * LD-T4-5: the organizer's "Packet documents" surface — a card on the Asks tab (not a 7th tab; the
 * mobile tab strip is already at six). Upload / rename / delete PDF attachments for the event, with
 * a live quota line and a standing caution. Server 400 messages are surfaced VERBATIM (the
 * NEW-FEATURE-CHECKLIST "surface the server's error" rule). Semantic tokens only.
 */
export function PacketDocumentsCard({ event }: { event: LobbyDayEventDetailDto }) {
  const { data, isLoading } = useEventAttachments(event.id)
  const upload = useUploadAttachment(event.id)
  const update = useUpdateAttachment(event.id)
  const remove = useDeleteAttachment(event.id)
  const fileInput = useRef<HTMLInputElement>(null)
  const [clientError, setClientError] = useState<string | null>(null)

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClientError(null)
    const file = e.target.files?.[0]
    e.target.value = ''   // allow re-picking the same file
    if (!file) return
    // Client pre-check: a non-PDF never reaches the server. The server re-validates by magic bytes.
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setClientError('Only PDF files are accepted.')
      return
    }
    upload.mutate({ file })
  }

  const onRename = (id: string, current: string) => {
    const next = window.prompt('Rename document', current)
    if (next && next.trim() && next.trim() !== current) {
      update.mutate({ attachmentId: id, title: next.trim() })
    }
  }

  const onDelete = (id: string) => {
    if (window.confirm('Delete this document? Attendees will no longer see it.')) {
      remove.mutate(id)
    }
  }

  const items = data?.items ?? []
  const limits = data?.limits
  const serverError = clientError
    ?? (upload.isError ? extractApiMessage(upload.error, 'Upload failed.') : null)

  return (
    <Card mt={4}>
      <HStack justify="space-between" mb={1}>
        <Text fontWeight="semibold">Packet documents</Text>
        {limits && (
          <Text fontSize="xs" color="fg.muted">
            {items.length} of {limits.maxFiles} · {formatMb(data!.usedBytes)} of {formatMb(limits.maxTotalBytes)}
          </Text>
        )}
      </HStack>

      <Text fontSize="xs" color="fg.muted" mb={3}>{CAUTION}</Text>

      {serverError && (
        <Text role="alert" fontSize="sm" color="danger.fg" mb={2}>{serverError}</Text>
      )}

      {isLoading ? (
        <Text fontSize="sm" color="fg.muted">Loading…</Text>
      ) : (
        <VStack align="stretch" gap={2}>
          {items.map((a) => (
            <HStack key={a.id} justify="space-between" minH="44px" px={2} py={1}
                    borderWidth="1px" borderRadius="md">
              <Link href={lobbyAttachmentApi.fileUrl(event.id, a.id)} fontSize="sm" fontWeight="medium">
                {a.title}
              </Link>
              <HStack gap={1}>
                <Text fontSize="xs" color="fg.muted">{formatMb(a.sizeBytes)}</Text>
                <Button aria-label="Rename" size="xs" variant="ghost" minH="44px"
                        onClick={() => onRename(a.id, a.title)}><FiEdit2 /></Button>
                <Button aria-label="Delete" size="xs" variant="ghost" minH="44px"
                        onClick={() => onDelete(a.id)}><FiTrash2 /></Button>
              </HStack>
            </HStack>
          ))}
          {items.length === 0 && (
            <Text fontSize="sm" color="fg.muted">No documents yet.</Text>
          )}
        </VStack>
      )}

      <Box mt={3}>
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf"
          style={{ display: 'none' }}
          onChange={onPick}
          data-testid="packet-document-input"
        />
        <Button size="sm" minH="44px" onClick={() => fileInput.current?.click()}
                loading={upload.isPending} colorPalette="brand">
          <FiUpload /> Upload PDF
        </Button>
      </Box>
    </Card>
  )
}
